import React from 'react';
import { Metronome } from '@/components/icons/Metronome';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// BrandMark — the single place the Cadence icon + wordmark is rendered.
//
// Centralising means the brand decision (Cadence in-app, Stratium on sign-in
// and in attribution only) cannot drift. Future changes land in one file.
//
// Performance: image asset is one of three pre-generated sizes in /public.
// Picking the nearest pre-sized asset keeps the decoded-image memory small
// and lets the browser cache the icon across surfaces.
// ---------------------------------------------------------------------------

export type BrandMarkSize = 'sm' | 'md' | 'lg';

interface BrandMarkProps {
  /** sm: sidebar (28px). md: header / signup (40px). lg: login hero (56px). */
  size?: BrandMarkSize;
  /** Show the "Cadence" wordmark next to the icon. Defaults to true. */
  showWordmark?: boolean;
  /** Show the "by Stratium" line below the wordmark. Use only on auth pages
   *  and the sidebar bottom attribution. Do not repeat elsewhere. */
  showStratiumLine?: boolean;
  /** Render "by Stratium" inline next to the wordmark (matching the redesign
   *  Login.html spec) rather than as a separate line beneath it. Ignored
   *  unless `showStratiumLine` is also true. */
  inlineStratiumTagline?: boolean;
  /** Override label. Defaults to "Cadence". */
  label?: string;
  /** Accent colour for the wordmark gradient. Defaults to theme primary. */
  accent?: 'primary' | 'light';
}

const ICON_PX: Record<BrandMarkSize, number> = {
  sm: 28,
  md: 40,
  lg: 56,
};

// Pre-sized transparent-background variants of the Cadence metronome mark.
// `sm` uses the 48px favicon (simplified mark — body + pendulum, no sound
// arcs) so the silhouette stays legible at small sizes; `md`/`lg` use the
// 192/512 hero mark with motion arcs. Replaces the old cadence-mark-28/
// 40/56 png triplet with the canonical favicon set shipped 2026-05-29.
const ICON_SRC: Record<BrandMarkSize, string> = {
  sm: '/favicon-48.png',
  md: '/android-chrome-192.png',
  lg: '/android-chrome-512.png',
};

const WORDMARK_PX: Record<BrandMarkSize, number> = {
  sm: 18,
  md: 22,
  lg: 28,
};

export function BrandMark({
  size = 'md',
  showWordmark = true,
  showStratiumLine = false,
  inlineStratiumTagline = false,
  label = 'Cadence',
  accent = 'primary',
}: BrandMarkProps) {
  const iconPx = ICON_PX[size];
  const iconSrc = ICON_SRC[size];
  const wordmarkPx = WORDMARK_PX[size];

  return (
    <div className="flex items-center gap-2.5">
      {size === 'lg' ? (
        // Hero size — the PNG carries motion-arc detail the inline
        // SVG can't render. Reserved for the cinematic Brief / login
        // hero, where the mark sits at 56px+ with room to breathe.
        <img
          src={iconSrc}
          alt=""
          aria-hidden
          width={iconPx}
          height={iconPx}
          className="block rounded-md"
        />
      ) : (
        // Sidebar and login surfaces use the inline SVG so the body
        // recolours with the surrounding foreground (slate on light,
        // near-white on dark) while the pendulum stays amber. The
        // `accent === 'light'` variant forces white-ish for the
        // dark cinematic auth backgrounds where text-foreground would
        // resolve to the wrong end of the palette.
        <Metronome
          size={iconPx}
          className={cn('block', accent === 'light' ? 'text-white' : 'text-foreground')}
          title=""
        />
      )}
      {showWordmark && (
        <div className={inlineStratiumTagline ? 'flex items-baseline gap-2' : undefined}>
          <span
            className={cn(
              'block font-bold leading-none',
              accent === 'light' ? 'text-[#F8FAFC]' : 'text-primary',
            )}
            style={{ fontSize: wordmarkPx, letterSpacing: '-0.02em' }}
          >
            {label}
          </span>
          {showStratiumLine && (
            <span
              className={cn(
                inlineStratiumTagline
                  ? 'text-[12px] font-normal lowercase text-muted-foreground'
                  : 'mt-0.5 block text-[11px] font-medium uppercase text-muted-foreground',
              )}
              style={{ letterSpacing: inlineStratiumTagline ? '0.01em' : '0.04em' }}
            >
              by Stratium
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default BrandMark;
